<script setup>
import { computed } from 'vue';
import { useToggle } from '@vueuse/core';
import { useI18n } from 'vue-i18n';
import { dynamicTime } from 'shared/helpers/timeHelper';
import { usePolicy } from 'dashboard/composables/usePolicy';
import {
  isPdfDocument,
  formatDocumentLink,
} from 'shared/helpers/documentHelper';

import CardLayout from 'dashboard/components-next/CardLayout.vue';
import DropdownMenu from 'dashboard/components-next/dropdown-menu/DropdownMenu.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  id: {
    type: Number,
    required: true,
  },
  name: {
    type: String,
    default: '',
  },
  assistant: {
    type: Object,
    default: () => ({}),
  },
  externalLink: {
    type: String,
    required: true,
  },
  createdAt: {
    type: Number,
    required: true,
  },
  status: {
    type: String,
    default: '',
  },
  canRetry: {
    type: Boolean,
    default: false,
  },
  crawlError: {
    type: String,
    default: '',
  },
  crawlErrorAt: {
    type: Number,
    default: null,
  },
  isRetrying: {
    type: Boolean,
    default: false,
  },
  rejectedCount: {
    type: Number,
    default: 0,
  },
});

const emit = defineEmits(['action']);
const { checkPermissions } = usePolicy();

const { t } = useI18n();

const [showActionsDropdown, toggleDropdown] = useToggle();

const menuItems = computed(() => {
  const allOptions = [
    {
      label: t('AILAB.DOCUMENTS.OPTIONS.VIEW_CRAWL_DETAILS'),
      value: 'viewCrawlDetails',
      action: 'viewCrawlDetails',
      icon: 'i-lucide-info',
    },
    {
      label: t('AILAB.DOCUMENTS.OPTIONS.VIEW_RELATED_RESPONSES'),
      value: 'viewRelatedQuestions',
      action: 'viewRelatedQuestions',
      icon: 'i-ph-tree-view-duotone',
    },
  ];

  if (checkPermissions(['administrator'])) {
    allOptions.push({
      label: t('AILAB.DOCUMENTS.OPTIONS.DELETE_DOCUMENT'),
      value: 'delete',
      action: 'delete',
      icon: 'i-lucide-trash',
    });
  }

  return allOptions;
});

const createdAt = computed(() => dynamicTime(props.createdAt));
const crawlErrorAt = computed(() =>
  props.crawlErrorAt ? dynamicTime(props.crawlErrorAt) : ''
);

const displayLink = computed(() => formatDocumentLink(props.externalLink));
const linkIcon = computed(() =>
  isPdfDocument(props.externalLink) ? 'i-ph-file-pdf' : 'i-ph-link-simple'
);

const statusKey = computed(() => props.status?.toString()?.toUpperCase() || '');
const statusLabel = computed(() => {
  const key = statusKey.value;
  if (!key) return '';
  return t(`AILAB.DOCUMENTS.STATUS.${key}`) || props.status;
});

const statusClass = computed(() => {
  switch (statusKey.value) {
    case 'AVAILABLE':
      return 'bg-n-teal-3 text-n-teal-11';
    case 'IN_PROGRESS':
      return 'bg-n-amber-3 text-n-amber-11';
    case 'BLOCKED':
    case 'FAILED':
      return 'bg-n-ruby-3 text-n-ruby-11';
    default:
      return 'bg-n-slate-3 text-n-slate-11';
  }
});

const statusHint = computed(() => {
  switch (statusKey.value) {
    case 'BLOCKED':
      return t('AILAB.DOCUMENTS.STATUS_HINT.BLOCKED');
    case 'FAILED':
      return t('AILAB.DOCUMENTS.STATUS_HINT.FAILED');
    case 'IN_PROGRESS':
      return t('AILAB.DOCUMENTS.STATUS_HINT.IN_PROGRESS');
    default:
      return '';
  }
});

const handleAction = ({ action, value }) => {
  toggleDropdown(false);
  emit('action', { action, value, id: props.id });
};
</script>

<template>
  <CardLayout>
    <div class="flex gap-1 justify-between w-full">
      <div class="flex gap-2 items-center min-w-0">
        <span class="text-base text-n-slate-12 line-clamp-1 min-w-0">
          {{ name }}
        </span>
        <span
          v-if="statusLabel"
          class="px-2 py-0.5 text-xs font-medium rounded-full shrink-0"
          :class="statusClass"
          :title="statusHint || statusLabel"
        >
          {{ statusLabel }}
        </span>
        <span
          v-if="rejectedCount > 0"
          class="px-2 py-0.5 text-xs font-medium rounded-full shrink-0 bg-n-slate-3 text-n-slate-11"
          :title="
            t('AILAB.DOCUMENTS.REJECTED_BADGE_TOOLTIP', {
              count: rejectedCount,
            })
          "
        >
          {{ t('AILAB.DOCUMENTS.REJECTED_BADGE', { count: rejectedCount }) }}
        </span>
      </div>
      <div class="flex gap-2 items-center">
        <Button
          v-if="canRetry"
          :label="t('AILAB.DOCUMENTS.RETRY.BUTTON_LABEL') || 'Retry'"
          icon="i-lucide-rotate-cw"
          color="slate"
          size="xs"
          variant="outline"
          class="rounded-md"
          :is-loading="isRetrying"
          :disabled="isRetrying"
          @click="emit('action', { action: 'retry', id })"
        />
        <div
          v-on-clickaway="() => toggleDropdown(false)"
          class="flex relative items-center group"
        >
          <Button
            icon="i-lucide-ellipsis-vertical"
            color="slate"
            size="xs"
            class="rounded-md group-hover:bg-n-alpha-2"
            @click="toggleDropdown()"
          />
          <DropdownMenu
            v-if="showActionsDropdown"
            :menu-items="menuItems"
            class="top-full mt-1 ltr:right-0 rtl:left-0 xl:ltr:right-0 xl:rtl:left-0"
            @action="handleAction($event)"
          />
        </div>
      </div>
    </div>
    <div class="flex gap-4 justify-between items-center w-full">
      <span
        class="flex gap-1 items-center text-sm truncate shrink-0 text-n-slate-11"
      >
        <i class="i-ph-robot-bold" />
        {{ assistant?.name || '' }}
      </span>
      <span
        class="flex flex-1 gap-1 justify-start items-center text-sm truncate text-n-slate-11"
      >
        <i :class="linkIcon" class="shrink-0" />
        <span class="truncate">{{ displayLink }}</span>
      </span>
      <div class="text-sm shrink-0 text-n-slate-11 line-clamp-1">
        {{ createdAt }}
      </div>
    </div>

    <div
      v-if="crawlError"
      class="mt-2 text-xs text-n-ruby-9 line-clamp-2"
      :title="crawlError"
    >
      <span class="font-medium">
        {{ t('AILAB.DOCUMENTS.ERROR_LABEL') || 'Crawl error' }}:
      </span>
      {{ crawlError }}
      <span v-if="crawlErrorAt" class="text-n-slate-11">
        ({{ crawlErrorAt }})
      </span>
    </div>

    <div
      v-else-if="statusHint"
      class="mt-2 text-xs text-n-slate-11 line-clamp-2"
    >
      {{ statusHint }}
    </div>
  </CardLayout>
</template>
